import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Phone, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import apiService from '../../services/api';
import { VendorSearchDto, WorkOrderVendor, Category, VendorCompliance } from '../../types';
import { getComplianceStatusClasses, isNonCompliant, requiresComplianceAcknowledgment } from '../../utils/vendorCompliance';
import { formatPhoneNumber } from '../../utils/formatters';
import ComplianceOverrideModal from './ComplianceOverrideModal';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface VendorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVendor: (
    vendor: VendorSearchDto,
    complianceOverride?: { acknowledgeComplianceWarning: boolean; complianceOverrideReason: string }
  ) => void;
  clientZipCode: string;
  existingVendors: WorkOrderVendor[];
}

const VendorSelectionModal: React.FC<VendorSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectVendor,
  clientZipCode,
  existingVendors
}) => {
  const [radius, setRadius] = useState(25);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [pendingVendor, setPendingVendor] = useState<VendorSearchDto | null>(null);
  const [complianceWarning, setComplianceWarning] = useState<VendorCompliance | null>(null);
  const [showComplianceOverrideModal, setShowComplianceOverrideModal] = useState(false);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');

  // Get existing vendor IDs to filter them out
  const existingVendorIds = useMemo(() => 
    existingVendors.map(v => v.vendorId), 
    [existingVendors]
  );

  // Fetch categories for filtering
  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100), // Get up to 100 categories
    enabled: isOpen
  });

  const categories = categoriesResponse?.data?.data || [];

  // Fetch vendors based on client zip code and radius
  const { data: vendorsResponse, isLoading } = useQuery({
    queryKey: ['vendors-search', clientZipCode, radius, selectedCategories],
    queryFn: () => {
      const searchZipCode = clientZipCode || '10001';
      const searchCategories = selectedCategories.length > 0 ? selectedCategories : [];
      console.log('Vendor search params:', { zipcode: searchZipCode, radius, categoryIds: searchCategories });
      return apiService.searchVendors(searchZipCode, radius, searchCategories);
    },
    enabled: isOpen && !!clientZipCode
  });

  const vendors = useMemo(() => vendorsResponse?.data || [], [vendorsResponse?.data]);

  // Filter out already selected vendors
  const availableVendors = useMemo(() => 
    vendors.filter(vendor => !existingVendorIds.includes(vendor.id)),
    [vendors, existingVendorIds]
  );

  const handleVendorSelect = useCallback(async (vendor: VendorSearchDto) => {
    if (hasVendorCompliance && requiresComplianceAcknowledgment(vendor.complianceStatus, vendor.complianceStatusLabel)) {
      setPendingVendor(vendor);
      setIsLoadingCompliance(true);
      try {
        const response = await apiService.getVendorCompliance(vendor.id);
        if (response.success && response.data) {
          setComplianceWarning(response.data);
          setShowComplianceOverrideModal(true);
        } else {
          toast.error('Unable to verify vendor compliance. Assignment blocked until compliance is confirmed.');
        }
      } catch {
        toast.error('Unable to verify vendor compliance. Please try again.');
      } finally {
        setIsLoadingCompliance(false);
      }
      return;
    }

    onSelectVendor(vendor);
    onClose();
  }, [hasVendorCompliance, onSelectVendor, onClose]);

  const handleComplianceOverrideConfirm = (reason: string) => {
    if (!pendingVendor) return;

    onSelectVendor(pendingVendor, {
      acknowledgeComplianceWarning: true,
      complianceOverrideReason: reason,
    });
    setShowComplianceOverrideModal(false);
    setComplianceWarning(null);
    setPendingVendor(null);
    onClose();
  };

  const handleCategoryToggle = useCallback((categoryId: number) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Vendor"
      maxWidth="6xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Search for vendors near {clientZipCode || 'your location'}
        </p>

        {/* Search Controls */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          {!clientZipCode && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="text-sm text-yellow-800">
                <strong>Note:</strong> No zip code available for this client. Search will use a default location.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Radius Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Radius
              </label>
              <select
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color"
              >
                <option value={10}>10 miles</option>
                <option value={25}>25 miles</option>
                <option value={50}>50 miles</option>
                <option value={100}>100 miles</option>
              </select>
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Categories
              </label>
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Select categories to find specialized vendors
              </div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {Array.isArray(categories) && categories.length > 0 ? (
                  categories.map((category: Category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryToggle(category.id)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        selectedCategories.includes(category.id)
                          ? 'bg-accent-color-100 text-accent-color-800 border-accent-color-300'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">No categories available</div>
                )}
              </div>
              {selectedCategories.length > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
                  </div>
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-xs text-accent-color hover:text-accent-color-800 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vendor List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">Searching for vendors...</div>
            </div>
          ) : availableVendors.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                {vendors.length === 0 
                  ? selectedCategories.length > 0 
                    ? `No vendors found in selected categories within ${radius} miles`
                    : `No vendors found within ${radius} miles`
                  : 'All available vendors are already assigned to this work order'
                }
              </div>
              {selectedCategories.length > 0 && (
                <button
                  onClick={() => setSelectedCategories([])}
                  className="mt-4 text-sm text-accent-color hover:text-accent-color-800 underline"
                >
                  Try without category filters
                </button>
              )}
            </div>
          ) : (
            <div>
              {/* Search Summary */}
              <div className="mb-4 p-3 bg-accent-color-50 border border-accent-color-200 rounded-md">
                <div className="text-sm text-accent-color-800">
                  <strong>Search Results:</strong> Found {availableVendors.length} vendor{availableVendors.length !== 1 ? 's' : ''} 
                  within {radius} miles
                  {selectedCategories.length > 0 && (
                    <span> in {selectedCategories.length} selected categor{selectedCategories.length === 1 ? 'y' : 'ies'}</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                {availableVendors.map((vendor) => {
                const needsOverride = hasVendorCompliance && requiresComplianceAcknowledgment(vendor.complianceStatus, vendor.complianceStatusLabel);
                const vendorIsNonCompliant = hasVendorCompliance && isNonCompliant(vendor.complianceStatus, vendor.complianceStatusLabel);
                return (
                <div
                  key={vendor.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    vendorIsNonCompliant
                      ? 'border-red-300 bg-red-50/50 hover:bg-red-50 dark:border-red-700 dark:bg-red-900/10 dark:hover:bg-red-900/20'
                      : needsOverride
                        ? 'border-yellow-300 bg-yellow-50/50 hover:bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/10'
                        : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
                  } cursor-pointer`}
                  onClick={() => !isLoadingCompliance && handleVendorSelect(vendor)}
                  title={needsOverride ? 'Compliance override required to assign this vendor' : undefined}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{vendor.name}</h3>
                        {vendor.isActive && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                        {hasVendorCompliance && vendor.complianceStatusLabel && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(vendor.complianceStatus, vendor.complianceStatusLabel)}`}>
                            {vendor.complianceStatusLabel}
                          </span>
                        )}
                        {needsOverride && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
                            Override required
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        {vendor.address && (
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{vendor.address}</span>
                          </div>
                        )}
                        
                        {vendor.phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="h-4 w-4" />
                            <span>{formatPhoneNumber(vendor.phone)}</span>
                          </div>
                        )}
                        
                        {vendor.distance && (
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{vendor.distance.toFixed(1)} miles away</span>
                          </div>
                        )}
                      </div>

                      {vendor.categories && vendor.categories.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {vendor.categories.map((category) => (
                              <span
                                key={category.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-color-100 text-accent-color-800"
                              >
                                {category.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4">
                      <button className="inline-flex items-center px-3 py-1 border border-accent-color text-accent-color rounded-md hover:bg-accent-color-50 transition-colors">
                        <Check className="h-4 w-4 mr-1" />
                        Select
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {hasVendorCompliance && (
      <ComplianceOverrideModal
        isOpen={showComplianceOverrideModal}
        onClose={() => {
          setShowComplianceOverrideModal(false);
          setComplianceWarning(null);
          setPendingVendor(null);
        }}
        compliance={complianceWarning}
        onConfirm={handleComplianceOverrideConfirm}
        isLoading={isLoadingCompliance}
      />
      )}
    </Modal>
  );
};

export default VendorSelectionModal;
